import React, { useEffect, useMemo, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { PrimaryButton, Dialog, Toast, TOAST_VISIBILITY_DURATIONS, TOAST_POSITIONS, TOAST_VARIANTS } from 'cdk-radial';
import { setScreen } from '../../../../redux/connectedComponent/uiFactory/uiFactory.slice';
import { screens } from '../../../config/uiFactory.config';
import { getCppForAppAndLocations, updateCppForApp, clearCppUpdateError } from '../../../../redux/connectedComponent/privacyProtection/privacyProtection.slice';
import { getDMSInfoBasedOnApis } from '../../../../redux/connectedComponent/selectedDMS/selectedDMS.slice';
import { Loading } from '../../../loader/Loading';
import Page from '../../../page';
import SolutionHeader from '../../SolutionHeader/SolutionHeader';
import SelectPrivacyProtection from '../../../stepper/stepperBody/stepperBodyComponents/SelectPrivacyProtection';
import { sendAmplitudeData } from '../../../../utils/amplitude';
import { AMPLITUDE_EVENTS, AMPLITUDE_PROPERTIES } from '../../../../constants/amplitudeConstants';
import { trackPageView } from '../../../../utils/gtm';

const EditCppWrapper = styled.div`
  margin: 2rem 0;
`;

const CppSaveFooterWrapper = styled.div`
  display: -ms-flexbox;
  display: flex;
  justify-content: center;
`;

const CppErrorWrapper = styled.div`
  text-align: center;
`;


function CppSaveFooter({ saveCppSettings }) {
  return (
    <CppSaveFooterWrapper>
      <PrimaryButton text="Save" onClick={saveCppSettings} />
    </CppSaveFooterWrapper>
  )
}

export default function EditPrivacyProtection({
  solutionData,
  additionalData: { subscriptionData, isAppNotListed, isBulkSubscribed }
}) {
  const dispatch = useDispatch();
  const {
    authenticationToken,
    allLocations,
    entityIdNameMapping,
    entityIdAddressMapping
  } = useSelector(state => state.loginDetailsSlice);
  const {
    getDMSInfoBasedOnApisPending,
    getDMSInfoBasedOnApisError,
    privacyProtectionSupported
  } = useSelector(state => state.selectDMSSlice);
  const {
    availableLocations,
    optOutsByLocation,
    loadingOptOuts,
    loadingOptOutsError,
    updateCppPending,
    updateCppError,
    solutionInfo,
    fetchingSolution,
    getSolutionError
  } = useSelector(state => state.privacyProtectionSlice);
  const solution = isAppNotListed && !fetchingSolution  ? solutionInfo : solutionData;

  const [showSaveToast, setShowSaveToast] = useState(false);

  const privacyProtectionApiIds = useMemo(() => {
    return Object.entries(privacyProtectionSupported)
      .filter(([, v]) => v)
      .map(([k]) => k);
  }, [privacyProtectionSupported]);


  useEffect(() => {
    trackPageView('Manage Privacy Protection');
    if (authenticationToken && solution?.id && !fetchingSolution) {
      let locations;
      if (isBulkSubscribed) {
        locations = subscriptionData.map((sub) => ({
          location: allLocations.find(
            (entity) => entity.entityId === sub.entityId
          )?.partyName,
          entityId: sub.entityId,
          cmfNumber: sub.entitycmfNumber,
        }));
      } else {
        locations = subscriptionData.map((sub) => ({
          location: sub.entityIdList.find(
            (entity) => entity.entityId === sub.entityId
          )?.location,
          entityId: sub.entityId,
          cmfNumber: sub.entitycmfNumber,
        }));
      }

      locations = locations.map(entity => ({
        ...entity,
        location: entityIdNameMapping[entity.entityId],
        address: entityIdAddressMapping[entity.entityId]
      }));

      dispatch(getCppForAppAndLocations({
        appId: solution.id,
        locations,
        apiDetails: solution.registration_details?.api_details,
        token: authenticationToken
      }));
    }
  }, [solution, subscriptionData, authenticationToken]);

  useEffect(() => {
    if (authenticationToken && solution?.id && !fetchingSolution) {
      dispatch(getDMSInfoBasedOnApis({
        apis: solution.registration_details?.api_details.map(api => ({
          id: api.id,
          apiType: api.apiType
        })),
        token: authenticationToken
      }));
    }
  }, [solution, authenticationToken]);

  function onBackButtonClick() {
    dispatch(setScreen({ screen: screens.SUBSCRIPTION_MANAGEMENT }));
  }

  function saveCppSettings() {

    sendAmplitudeData(AMPLITUDE_EVENTS.MANAGE_CPP, {
      [AMPLITUDE_PROPERTIES.APP_NAME]: solution?.overview?.name || solution?.name,
      [AMPLITUDE_PROPERTIES.APP_ID]: solution?.id,
      [AMPLITUDE_PROPERTIES.APIS]: solution?.registration_details?.api_details
        .filter((item) => privacyProtectionSupported.hasOwnProperty(item.id))
        .map((api) => ({
          [AMPLITUDE_PROPERTIES.API_ID]: api?.id,
          [AMPLITUDE_PROPERTIES.API_NAME]: api?.displayName || api?.name,
        })),
    });

    dispatch(updateCppForApp({
      appId: solutionData.id,
      optOuts: optOutsByLocation,
      locations: availableLocations,
      cppSupportedApis: privacyProtectionApiIds,
      token: authenticationToken
    }))
      .then(unwrapResult)
      .then(() => setShowSaveToast(true))
      .catch(err => console.error(err));
  }

  return (
    <Page>
      <SolutionHeader
        solutionData={solutionData}
        onBackButtonClickHandler={onBackButtonClick}
        appName={solutionData?.displayName}
        publisherName={solutionData?.orgName}
        isAppNotListed={isAppNotListed}
      />
      {(loadingOptOuts || getDMSInfoBasedOnApisPending || updateCppPending || fetchingSolution) ? (
        <Loading />
      ) : (
        (loadingOptOutsError || getDMSInfoBasedOnApisError || getSolutionError) ? (
          <CppErrorWrapper>
            <h1>Something went wrong.</h1>
            <h2>
              Please contact Fortellis Support at support@fortellis.io
            </h2>
          </CppErrorWrapper>
        ) : (
          <EditCppWrapper>
            <SelectPrivacyProtection
              solutionData={solution}
            />
            <CppSaveFooter saveCppSettings={saveCppSettings} />
          </EditCppWrapper>
        )
      )}
      <Dialog
        id="privacy-protection-edit-error"
        title="Failed to update Consumer Privacy Protection"
        isOpen={!!updateCppError}
        onClose={() => dispatch(clearCppUpdateError())}
      >
        <p>An error occurred while updating Consumer Privacy Protection settings.</p>
        <p>
          Please contact Fortellis Support at{' '}
          <a href="mailto:support@fortellis.io">support@fortellis.io</a>.
        </p>
      </Dialog>
      {showSaveToast && (
        <Toast
          id="edit-privacy-protection-success"
          content="Consumer Privacy Protection settings saved."
          variant={TOAST_VARIANTS.POSITIVE}
          visibilityDuration={TOAST_VISIBILITY_DURATIONS.SIX_SECONDS}
          position={TOAST_POSITIONS.FIXED}
          onVisibilityDurationEnd={() => setShowSaveToast(false)}
        />
      )}
    </Page>
  )
}
